import { Capacitor, registerPlugin } from "@capacitor/core";

const NativeCardMotion = registerPlugin("NativeCardMotion");
const subscribers = new Set();
let queue = Promise.resolve();
let stopSource;

// One sensor subscription, including across overlapping modal exit/enter cycles.
function reconcile() {
  queue = queue.then(async () => {
    if (!subscribers.size) {
      if (stopSource) await stopSource();
      stopSource = undefined;
      return;
    }
    if (stopSource) return;
    const emit = (sample) => subscribers.forEach((listener) => listener(sample));
    if (Capacitor.isNativePlatform()) {
      if (!Capacitor.isPluginAvailable("NativeCardMotion")) return;
      const handle = await NativeCardMotion.addListener("orientation", emit);
      try {
        const { available } = await NativeCardMotion.start();
        if (!available) {
          await handle.remove();
          return;
        }
        stopSource = async () => {
          await NativeCardMotion.stop().catch(() => {});
          await handle.remove();
        };
      } catch {
        await handle.remove();
      }
    } else if (typeof window !== "undefined") {
      // Listen passively; never surprise someone with a permission sheet while
      // they are rating another person. Browsers requiring consent stay static.
      const receive = ({ alpha, beta, gamma }) => {
        if (![alpha, beta, gamma].every(Number.isFinite)) return;
        const [z, x, y] = [alpha, beta, gamma].map((v) => v * Math.PI / 360);
        const [cz, cx, cy] = [z, x, y].map(Math.cos);
        const [sz, sx, sy] = [z, x, y].map(Math.sin);
        // DeviceOrientation uses intrinsic Z-X-Y Euler angles.
        emit({
          x: cz * sx * cy - sz * cx * sy,
          y: cz * cx * sy + sz * sx * cy,
          z: sz * cx * cy + cz * sx * sy,
          w: cz * cx * cy - sz * sx * sy,
        });
      };
      window.addEventListener("deviceorientation", receive);
      stopSource = async () => window.removeEventListener("deviceorientation", receive);
    }
  }).catch(() => {});
}

export function subscribeCardMotion(listener) {
  subscribers.add(listener);
  reconcile();
  return () => {
    subscribers.delete(listener);
    reconcile();
  };
}
